import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { appConfig } from './config';
import { Theme } from './types';
import { applyTheme, getInitialTheme } from './utils/theme';
import LandingPage from './pages/LandingPage';
import Layout from './components/Layout';
import { CartProvider } from './context/CartContext';
import ShopModeBanner from './components/shop/ShopModeBanner';

const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const PredictionsPage = lazy(() => import('./pages/PredictionsPage'));
const PropsPage = lazy(() => import('./pages/PropsPage'));
const PlayerPage = lazy(() => import('./pages/PlayerPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutCompletePage = lazy(() => import('./pages/CheckoutCompletePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const DataSourcesPage = lazy(() => import('./pages/DataSourcesPage'));
const DataDeskPage = lazy(() => import('./pages/DataDeskPage'));

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="route-loader" role="status">Setting type...</div>}>
      {children}
    </Suspense>
  );
}

const App: React.FC = () => {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(
        `%c[App] ${appConfig.appName} v${appConfig.version} initialized`,
        'color: #ff2e79; font-weight: bold;'
      );
    }
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <BrowserRouter>
      <CartProvider>
      <ShopModeBanner />
      <Routes>
        <Route path="/" element={<LandingPage theme={theme} onToggleTheme={toggleTheme} />} />
        {/* Standalone (no site nav/footer) — not linked anywhere, operator-only. */}
        <Route path="/admin/data-desk" element={<LazyPage><DataDeskPage theme={theme} onToggleTheme={toggleTheme} /></LazyPage>} />
        {appConfig.features.enableShop && (
          <>
            <Route path="/checkout" element={<LazyPage><CheckoutPage theme={theme} /></LazyPage>} />
            <Route path="/checkout/complete" element={<LazyPage><CheckoutCompletePage /></LazyPage>} />
          </>
        )}

        <Route element={<Layout theme={theme} onToggleTheme={toggleTheme} />}>
          <Route path="/games" element={<LazyPage><GamesPage /></LazyPage>} />
          {appConfig.features.enablePredictions && (
            <>
              <Route path="/predictions" element={<LazyPage><PredictionsPage /></LazyPage>} />
              <Route path="/predictions/record" element={<LazyPage><PredictionsPage /></LazyPage>} />
            </>
          )}
          {appConfig.features.enableProps && (
            <>
              <Route path="/props" element={<LazyPage><PropsPage /></LazyPage>} />
              <Route path="/props/:league/:playerId" element={<LazyPage><PlayerPage /></LazyPage>} />
            </>
          )}
          {appConfig.features.enableShop && (
            <>
              <Route path="/shop" element={<LazyPage><ShopPage /></LazyPage>} />
              <Route path="/cart" element={<LazyPage><CartPage /></LazyPage>} />
            </>
          )}
          <Route path="/blog" element={<LazyPage><BlogIndexPage /></LazyPage>} />
          <Route path="/blog/:slug" element={<LazyPage><BlogPostPage /></LazyPage>} />
          <Route path="/glossary" element={<LazyPage><GlossaryPage /></LazyPage>} />
          <Route path="/faq" element={<LazyPage><FaqPage /></LazyPage>} />
          <Route path="/privacy" element={<LazyPage><PrivacyPolicyPage /></LazyPage>} />
          <Route path="/terms" element={<LazyPage><TermsOfUsePage /></LazyPage>} />
          <Route path="/accessibility" element={<LazyPage><AccessibilityPage /></LazyPage>} />
          <Route path="/data-sources" element={<LazyPage><DataSourcesPage /></LazyPage>} />
          <Route path="*" element={<LazyPage><NotFoundPage /></LazyPage>} />
        </Route>
      </Routes>
      </CartProvider>
    </BrowserRouter>
  );
};

export default App;
